import { useQuery } from '@apollo/client';
import { useParams } from '@reach/router';
import type React from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { Pagination } from '@xing-com/crate-people-directory-pagination';
import { UserChunk } from '@xing-com/crate-people-directory-user-chunk';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';
import { Row, Column } from '@xing-com/xing-grid';

import { StyledHeadline, ContentRow } from './chunk-page-by-username.styles';
import { SEO_URL_META_DATA } from './constants';
import { PeopleChunkPageByUsernameDocument } from './graphql/chunk-page-by-username.query.gql-types';
import type {
  PeopleChunkPageByUsernameQuery,
  PeopleChunkPageByUsernameQueryVariables,
} from './graphql/chunk-page-by-username.query.gql-types';
import type { PageName } from './types';
import { PageNamesTypes } from './types';

export const ChunkPageByUsername: React.FC = () => {
  const { letter, pageLevel, page } = useParams();
  const { isLoggedOut } = useLoginState();

  const { data, error, loading } = useQuery<
    PeopleChunkPageByUsernameQuery,
    PeopleChunkPageByUsernameQueryVariables
  >(PeopleChunkPageByUsernameDocument, {
    variables: { letter, pageLevel: +pageLevel, page: +page },
    skip: !letter,
  });
  const { formatMessage } = useIntl();
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  useEffect(() => {
    if (!isLoggedOut) {
      window.location.assign('https://www.xing.com/search/members');
    }
  }, [isLoggedOut]);

  if (!isLoggedOut) {
    return;
  }

  const { names, name, toName, totalPages, cities, breadcrumbs } =
    data?.peopleChunkPage || {};

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  const basicPageLayoutProps = {
    seoTitle: formatMessage({ id: 'PD_100_HEADING' }),
    seoDescription: formatMessage({ id: 'PD_100_DESCR' }),
    index: true,
    letter,
    seoUrl: `${SEO_URL_META_DATA}/pages/${letter}/${pageLevel}/${page}/`,
    pageName: `${letter}/pages/${pageLevel}/${page}`,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  if (error || (!loading && !data)) {
    return (
      <PageLayout {...basicPageLayoutProps}>
        <UserMessage type={UserMessageType.error} />
      </PageLayout>
    );
  }

  if (!loading && (!data?.peopleChunkPage || !names || !names.length)) {
    return (
      <PageLayout {...basicPageLayoutProps}>
        <UserMessage type={UserMessageType.empty} />
      </PageLayout>
    );
  }

  const headlineMessage = `${name} ... ${toName}`;

  const seoTitle = formatMessage(
    { id: 'PD_LETTER_ROOT_TITLE' },
    { display_name: headlineMessage }
  );

  const description = formatMessage(
    { id: 'PD_META_DESCRIPTION_LVL_1' },
    { display_name: headlineMessage, cities: (cities || []).join(', ') }
  );
  const fullPageLayoutProps = {
    ...basicPageLayoutProps,
    seoTitle,
    seoDescription: description,
    sanitizedBreadcrumbs,
  };

  const headlineRow = (
    <Row>
      <Column sizeWide={12} size={0}>
        <StyledHeadline size="xxlarge">{headlineMessage}</StyledHeadline>
      </Column>
    </Row>
  );

  const sanitizedNames =
    names
      ?.map((name): PageName | null => {
        switch (name?.__typename) {
          case 'PeopleChildItem': {
            return {
              __typename: PageNamesTypes.child,
              name: name?.name || '',
              toName: name?.toName || '',
              url: name?.url || '',
            };
          }
          case 'PeopleUserBagItem': {
            return {
              __typename: PageNamesTypes.userBag,
              name: name?.name || '',
              count: name?.count || 0,
              url: name?.url || '',
            };
          }
          case 'PeopleProfileItem': {
            return {
              __typename: PageNamesTypes.profile,
              name: name?.name || '',
              photoUrl: name?.photoUrl || '',
              coach: name?.coach || false,
              url: name?.url || '',
              occupationTitle: name?.occupationTitle || '',
              city: name?.city || '',
            };
          }
          default: {
            return null;
          }
        }
      })
      .reduce<Array<PageName>>((result, name: PageName | null) => {
        if (name) {
          return [...result, name];
        } else {
          return result;
        }
      }, []) || [];

  const route = `/people/pages/:letter/:pageLevel/:page/`;

  return (
    <PageLayout {...fullPageLayoutProps}>
      {isDesktop ? headlineRow : null}
      <ContentRow $isLoading={loading}>
        <Column size={12}>
          {!isDesktop ? (
            <StyledHeadline size="large">{headlineMessage}</StyledHeadline>
          ) : null}
          {names ? <UserChunk names={sanitizedNames} fullWidthLayout /> : null}
        </Column>
        <Column size={12}>
          {page && totalPages && (
            <Pagination
              currentPage={page}
              pagesNumber={totalPages}
              route={route}
              routeParams={{ letter, pageLevel }}
            />
          )}
        </Column>
      </ContentRow>
    </PageLayout>
  );
};
